/**
 * FameRecognitionServiceModule — A8r Slice B (2026-06-04).
 *
 * Wraps the briefing-room backend's NPC Fame Recognition routes.
 * Slice B scope: read recognition outcomes + fire roll for newly-encountered
 * TAROT NPCs.
 *
 * Routes (per briefing_room_backend/routes/fame.py):
 *   GET    /api/modules/briefing-room/campaigns/{cid}/fame
 *          → FameRecognitionRegistry (recognitions[])
 *   POST   /api/modules/briefing-room/campaigns/{cid}/fame/roll
 *          → FameRecognitionEntry (200 new OR existing — idempotent on
 *            (npc_id, scenario_id))
 *
 * Auth: Bearer JWT from localStorage via getAuthHeaders() — mirrors
 * NpcEncounterServiceModule + HeroPointServiceModule pattern.
 */
import { getAuthHeaders } from './authHeaders';

export interface FameRecognitionEntry {
  id: string;
  npc_id: string;
  scenario_id: string;
  bond_fame_points: number;
  npc_fame_points: number;
  success_chance: number;
  /** A8r Slice C: nullable — override-only rows (created via POST /override
   *  without a prior natural roll) have null here. */
  d100_roll: number | null;
  /** A8r Slice C: EFFECTIVE recognized = gm_override ?? raw_recognized.
   *  Frontend consumers read this directly; the effective vs raw logic
   *  happens server-side in _row_to_entry. */
  recognized: boolean;
  /** A8r Slice C: raw override value — null when no override has been set.
   *  Exposed for forward-compat with Slice D's "manual badge" UI; Slice C
   *  itself does not render it. */
  gm_override?: boolean | null;
  occurred_at: string;
}

export interface FameRecognitionRegistry {
  recognitions: FameRecognitionEntry[];
}

/**
 * A8r Slice D (2026-06-04) — per-NPC aggregate across all scenarios in a campaign.
 * Backend computes on-read via GROUP BY (Option α, no schema change).
 */
export interface CrossScenarioRecognition {
  npc_id: string;
  /** True if ANY (npc, scenario) pair has effective recognition. */
  ever_recognized: boolean;
  /** ISO timestamp of the earliest recognized=true row; null if never. */
  first_recognized_at: string | null;
  /** scenario_id of the earliest recognized=true row; null if never. */
  first_scenario_id: string | null;
  /** Count of rows where gm_override IS NOT NULL right now (cleared = 0). */
  override_count: number;
}

export interface CrossScenarioRegistry {
  recognitions: CrossScenarioRecognition[];
}

const BASE_PATH = '/api/modules/briefing-room/campaigns';

export class FameRecognitionServiceModule {
  constructor(
    private readonly fetchFn: typeof fetch = (input, init) => fetch(input, init),
  ) {}

  private registryUrl(campaignId: string): string {
    return `${BASE_PATH}/${encodeURIComponent(campaignId)}/fame`;
  }

  private rollUrl(campaignId: string): string {
    return `${BASE_PATH}/${encodeURIComponent(campaignId)}/fame/roll`;
  }

  private overrideUrl(campaignId: string): string {
    return `${BASE_PATH}/${encodeURIComponent(campaignId)}/fame/override`;
  }

  private crossScenarioUrl(campaignId: string): string {
    return `${BASE_PATH}/${encodeURIComponent(campaignId)}/fame/cross_scenario`;
  }

  async getRegistry(campaignId: string): Promise<FameRecognitionRegistry> {
    const resp = await this.fetchFn(this.registryUrl(campaignId), {
      method: 'GET',
      headers: { ...getAuthHeaders() },
    });
    if (!resp.ok) {
      throw new Error(
        `FameRecognitionServiceModule: getRegistry HTTP ${resp.status}`,
      );
    }
    return (await resp.json()) as FameRecognitionRegistry;
  }

  /**
   * Fire a Fame Recognition roll. Server generates d100 unless `d100Roll`
   * is provided (used by tests + Playwright forced-failure/forced-success
   * paths). Idempotent on (npcId, scenarioId) — returns the cached row if
   * already rolled for this scenario.
   */
  async rollRecognition(
    campaignId: string,
    npcId: string,
    scenarioId: string,
    bondFamePoints: number,
    npcFamePoints: number,
    d100Roll?: number,
  ): Promise<FameRecognitionEntry> {
    const body: Record<string, number | string> = {
      npc_id: npcId,
      scenario_id: scenarioId,
      bond_fame_points: bondFamePoints,
      npc_fame_points: npcFamePoints,
    };
    if (typeof d100Roll === 'number') {
      body.d100_roll = d100Roll;
    }
    const resp = await this.fetchFn(this.rollUrl(campaignId), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...getAuthHeaders(),
      },
      body: JSON.stringify(body),
    });
    if (!resp.ok) {
      throw new Error(
        `FameRecognitionServiceModule: rollRecognition HTTP ${resp.status}`,
      );
    }
    return (await resp.json()) as FameRecognitionEntry;
  }

  /**
   * A8r Slice C/D — GM force-recognize override. UPSERT semantics, NOT
   * idempotent like rollRecognition. Slice D extends to accept `null` to
   * CLEAR the override (restore the natural roll outcome as effective).
   *
   * Backend response semantics:
   *   - recognized=true/false: 200 + the updated/inserted FameRecognitionEntry
   *   - recognized=null + prior row: 200 + entry with gm_override = null
   *   - recognized=null + no prior row: 204 No Content (idempotent no-op)
   *
   * Return type: `FameRecognitionEntry | null` — null is returned ONLY for
   * the 204 case (null + no prior row). The hook can treat that as a no-op
   * in its state-machine.
   */
  async setOverride(
    campaignId: string,
    npcId: string,
    scenarioId: string,
    recognized: boolean | null,
  ): Promise<FameRecognitionEntry | null> {
    const resp = await this.fetchFn(this.overrideUrl(campaignId), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...getAuthHeaders(),
      },
      body: JSON.stringify({
        npc_id: npcId,
        scenario_id: scenarioId,
        recognized,
      }),
    });
    if (!resp.ok) {
      throw new Error(
        `FameRecognitionServiceModule: setOverride HTTP ${resp.status}`,
      );
    }
    if (resp.status === 204) {
      return null;
    }
    return (await resp.json()) as FameRecognitionEntry;
  }

  /**
   * A8r Slice D (2026-06-04) — cross-scenario aggregate per NPC.
   * Returns per-NPC ever_recognized + first_recognized_at + first_scenario_id +
   * override_count. Used by useFameRecognition to merge cross-scenario carry
   * into effective recognition.
   */
  async getCrossScenario(campaignId: string): Promise<CrossScenarioRegistry> {
    const resp = await this.fetchFn(this.crossScenarioUrl(campaignId), {
      method: 'GET',
      headers: { ...getAuthHeaders() },
    });
    if (!resp.ok) {
      throw new Error(
        `FameRecognitionServiceModule: getCrossScenario HTTP ${resp.status}`,
      );
    }
    return (await resp.json()) as CrossScenarioRegistry;
  }
}
